import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Command Center Bundles Management
 * Create and manage product bundles - combinations of multiple products
 */
function CCBundles() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bundles, setBundles] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [saving, setSaving] = useState(false);

  // Position options
  const POSITIONS = {
    'entry': { name: 'Entry Level', color: '#17a2b8' },
    'core': { name: 'Core', color: '#28a745' },
    'premium': { name: 'Premium', color: '#6f42c1' }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, productsRes] = await Promise.all([
        fetch('/api/bundles', { credentials: 'include' }),
        fetch('/api/bundles/available-products', { credentials: 'include' })
      ]);

      if (bundlesRes.ok) {
        const bundlesData = await bundlesRes.json();
        setBundles(bundlesData.bundles || []);
      }

      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setAvailableProducts(productsData.products || []);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBundle({
      name: '',
      product_type: 'digital',
      purchase_type: 'one-time',
      sale_price: '',
      setup_fee: '',
      position: 'core',
      notes: '',
      usp: '',
      items: []
    });
    setShowModal(true);
  };

  const openEditModal = async (bundle) => {
    // Fetch full bundle details
    try {
      const res = await fetch(`/api/bundles/${bundle.id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEditingBundle(data.bundle);
        setShowModal(true);
      }
    } catch (err) {
      setError('Failed to load bundle details');
    }
  };

  const addProductToBundle = (productId) => {
    const product = availableProducts.find(p => p.id === parseInt(productId));
    if (product && !editingBundle.items.find(i => i.product_id === product.id)) {
      setEditingBundle({
        ...editingBundle,
        items: [...editingBundle.items, {
          product_id: product.id,
          product_name: product.name,
          product_price: product.sale_price,
          quantity: 1
        }]
      });
    }
  };

  const removeProductFromBundle = (productId) => {
    setEditingBundle({
      ...editingBundle,
      items: editingBundle.items.filter(i => i.product_id !== productId)
    });
  };

  const updateItemQuantity = (productId, quantity) => {
    setEditingBundle({
      ...editingBundle,
      items: editingBundle.items.map(i =>
        i.product_id === productId ? { ...i, quantity: parseInt(quantity) || 1 } : i
      )
    });
  };

  const calculateTotalValue = () => {
    return editingBundle?.items?.reduce((sum, item) => {
      return sum + ((item.product_price || 0) * (item.quantity || 1));
    }, 0) || 0;
  };

  const saveBundle = async () => {
    if (!editingBundle.name?.trim()) {
      setError('Bundle name is required');
      return;
    }

    if (!editingBundle.items || editingBundle.items.length === 0) {
      setError('Please add at least one product to the bundle');
      return;
    }

    setSaving(true);
    try {
      const isNew = !editingBundle.id;
      const url = isNew ? '/api/bundles' : `/api/bundles/${editingBundle.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingBundle,
          items: editingBundle.items.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity
          }))
        })
      });

      if (response.ok) {
        setShowModal(false);
        setSuccess(isNew ? 'Bundle created!' : 'Bundle updated!');
        setTimeout(() => setSuccess(''), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Error saving bundle');
    } finally {
      setSaving(false);
    }
  };

  const deleteBundle = async (bundle) => {
    if (!window.confirm(`Delete "${bundle.name}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`/api/bundles/${bundle.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Bundle deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Error deleting bundle');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading bundles...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/products')}
          >
            ← Back to Products
          </Button>
          <h1 className="mb-1">Bundles</h1>
          <p className="text-muted mb-0">
            Combine multiple products into value-packed bundles for promotions and special offers.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={openCreateModal}
          disabled={availableProducts.length === 0}
        >
          + New Bundle
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {availableProducts.length === 0 && (
        <Alert variant="info">
          You need to create some products first before you can bundle them.{' '}
          <Button variant="link" className="p-0" onClick={() => navigate('/command-center/products/catalog')}>
            Go to Product Catalog →
          </Button>
        </Alert>
      )}

      {/* Bundles Table */}
      {bundles.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
            <h4>No Bundles Yet</h4>
            <p className="text-muted mb-4">
              Bundles combine multiple products at a special price.
              Great for promotions and increasing average order value.
            </p>
            {availableProducts.length > 0 && (
              <Button
                variant="dark"
                style={{ backgroundColor: colors.greyDarkest }}
                onClick={openCreateModal}
              >
                Create Your First Bundle
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table responsive hover className="mb-0">
            <thead style={{ backgroundColor: colors.greyLightest }}>
              <tr>
                <th>Bundle Name</th>
                <th>Products</th>
                <th>Total Value</th>
                <th>Bundle Price</th>
                <th>Savings</th>
                <th>Position</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bundles.map(bundle => (
                <tr key={bundle.id}>
                  <td>
                    <strong>{bundle.name}</strong>
                    {bundle.notes && (
                      <div className="text-muted small" style={{ maxWidth: '250px' }}>
                        {bundle.notes.substring(0, 60)}{bundle.notes.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge bg="secondary">{bundle.item_count} product{bundle.item_count !== 1 ? 's' : ''}</Badge>
                  </td>
                  <td>{formatCurrency(bundle.total_value)}</td>
                  <td>
                    <strong>{formatCurrency(bundle.sale_price)}</strong>
                    {bundle.setup_fee && (
                      <div className="text-muted small">+ {formatCurrency(bundle.setup_fee)} setup</div>
                    )}
                  </td>
                  <td>
                    {bundle.savings > 0 ? (
                      <Badge bg="success">${bundle.savings.toLocaleString()} off</Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <Badge
                      bg="light"
                      text="dark"
                      style={{ borderLeft: `3px solid ${POSITIONS[bundle.position]?.color || '#6c757d'}` }}
                    >
                      {POSITIONS[bundle.position]?.name || bundle.position}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Button variant="link" size="sm" onClick={() => openEditModal(bundle)}>
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => deleteBundle(bundle)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Stats */}
      {bundles.length > 0 && (
        <Row className="mt-4">
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6f42c1' }}>
                  {bundles.length}
                </div>
                <div className="text-muted">Total Bundles</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.goldMid }}>
                  {bundles.reduce((sum, b) => sum + (b.item_count || 0), 0)}
                </div>
                <div className="text-muted">Products in Bundles</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                  ${bundles.reduce((sum, b) => sum + (b.savings || 0), 0).toLocaleString()}
                </div>
                <div className="text-muted">Total Savings Offered</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{editingBundle?.id ? 'Edit Bundle' : 'New Bundle'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingBundle && (
            <Form>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Bundle Name *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., 'Complete Marketing Bundle'"
                      value={editingBundle.name || ''}
                      onChange={(e) => setEditingBundle({ ...editingBundle, name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Position</strong></Form.Label>
                    <Form.Select
                      value={editingBundle.position || 'core'}
                      onChange={(e) => setEditingBundle({ ...editingBundle, position: e.target.value })}
                    >
                      {Object.entries(POSITIONS).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Products in Bundle */}
              <div className="border rounded p-3 mb-3" style={{ backgroundColor: colors.greyLightest }}>
                <Form.Label><strong>Products in This Bundle *</strong></Form.Label>

                <Form.Select
                  className="mb-3"
                  onChange={(e) => {
                    if (e.target.value) addProductToBundle(e.target.value);
                    e.target.value = '';
                  }}
                  value=""
                >
                  <option value="">+ Add a product...</option>
                  {availableProducts
                    .filter(p => !editingBundle.items?.find(i => i.product_id === p.id))
                    .map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.sale_price ? `($${p.sale_price})` : ''}
                      </option>
                    ))}
                </Form.Select>

                {editingBundle.items?.length > 0 ? (
                  <Table size="sm" className="mb-0 bg-white">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th style={{ width: '100px' }}>Price</th>
                        <th style={{ width: '80px' }}>Qty</th>
                        <th style={{ width: '100px' }}>Subtotal</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingBundle.items.map(item => (
                        <tr key={item.product_id}>
                          <td>{item.product_name}</td>
                          <td>{formatCurrency(item.product_price)}</td>
                          <td>
                            <Form.Control
                              type="number"
                              min="1"
                              size="sm"
                              value={item.quantity || 1}
                              onChange={(e) => updateItemQuantity(item.product_id, e.target.value)}
                              style={{ width: '60px' }}
                            />
                          </td>
                          <td>{formatCurrency((item.product_price || 0) * (item.quantity || 1))}</td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => removeProductFromBundle(item.product_id)}
                            >
                              ×
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                        <td colSpan="3" className="text-end">Total Value:</td>
                        <td colSpan="2">{formatCurrency(calculateTotalValue())}</td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-muted text-center py-3 bg-white rounded">
                    No products added yet. Select products above.
                  </div>
                )}
              </div>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Bundle Price</strong></Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="e.g., 297"
                        value={editingBundle.sale_price || ''}
                        onChange={(e) => setEditingBundle({ ...editingBundle, sale_price: e.target.value })}
                      />
                    </div>
                    {editingBundle.sale_price && calculateTotalValue() > 0 && (
                      <Form.Text className="text-success">
                        Savings: ${(calculateTotalValue() - parseFloat(editingBundle.sale_price || 0)).toLocaleString()}
                        {' '}({Math.round((1 - parseFloat(editingBundle.sale_price || 0) / calculateTotalValue()) * 100)}% off)
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Setup Fee</strong> (optional)</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="e.g., 97"
                        value={editingBundle.setup_fee || ''}
                        onChange={(e) => setEditingBundle({ ...editingBundle, setup_fee: e.target.value })}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      One-time fee (can be waived in promotions)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Purchase Type</strong></Form.Label>
                    <Form.Select
                      value={editingBundle.purchase_type || 'one-time'}
                      onChange={(e) => setEditingBundle({ ...editingBundle, purchase_type: e.target.value })}
                    >
                      <option value="one-time">One-Time Purchase</option>
                      <option value="recurring-monthly">Monthly Subscription</option>
                      <option value="recurring-annual">Annual Subscription</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label><strong>Bundle USP</strong> (What makes this bundle special?)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., 'Everything you need to launch your marketing in one package'"
                  value={editingBundle.usp || ''}
                  onChange={(e) => setEditingBundle({ ...editingBundle, usp: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Notes</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Internal notes about this bundle"
                  value={editingBundle.notes || ''}
                  onChange={(e) => setEditingBundle({ ...editingBundle, notes: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveBundle}
            disabled={saving}
          >
            {saving ? <Spinner animation="border" size="sm" /> : (editingBundle?.id ? 'Save Changes' : 'Create Bundle')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCBundles;
